import { ChatDrawerMessage, chatDrawerSelectors } from 'ducks/chatDrawer'
import { Button, Icon, styled } from 'opensolar-ui'
import React, { useEffect, useState } from 'react'
import Markdown from 'react-markdown'
import { useSelector } from 'react-redux'
import remarkGfm from 'remark-gfm'
import { useFeatureFlag } from 'util/featureFlags'
import Modal from '../../../elements/modal/Modal'
import { ActionConfirmation } from './ActionConfirmation'
import { AttachmentBadge } from './AttachmentBadge'
import { MessageActionsWrapper } from './MessageActionsWrapper'
import { ToolProgressRenderer } from './tools/ToolProgressRenderer'

interface MessageBubbleProps {
  message: ChatDrawerMessage
  isFailedSendMessage?: boolean
  messageIndex: number
}

const MessageWrapper = styled('div')<{ isSend: boolean }>(({ isSend }) => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'visible',
  ...(isSend
    ? {
        alignSelf: 'flex-end',
        maxWidth: '90%',
      }
    : {
        alignSelf: 'flex-start',
        width: '100%',
      }),
}))

const MessageBubbleContainer = styled('div')<{ isSend: boolean; isFailed?: boolean }>(({ isSend, isFailed }) => ({
  position: 'relative',
  padding: '12px 16px',
  borderRadius: 20,
  fontSize: 14,
  lineHeight: 1.5,
  wordWrap: 'break-word',
  overflow: 'visible',
  ...(isSend
    ? {
        backgroundColor: isFailed ? '#fef3c7' : '#e6f3ff',
        color: isFailed ? '#92400e' : '#292b2d',
        borderBottomRightRadius: 6,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
        ...(isFailed && {
          border: '1px dashed #f59e0b',
        }),
      }
    : {
        padding: '12px 0',
        color: '#292b2d',
      }),
}))

const ImageContainer = styled('div')({
  position: 'relative',
  display: 'inline-block',
  '&:hover $image': {
    opacity: 0.6,
  },
  '&:hover $imageOverlay': {
    opacity: 1,
  },
})

const MessageImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '300px',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'opacity 0.2s ease',
})

const ImageOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  transition: 'opacity 0.2s ease',
  opacity: 0,
})

const TableWrapper = styled('div')({
  overflowX: 'auto',
  margin: '12px 0',
  width: '100%',
})

const StyledTable = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
  margin: '12px 0',
  fontSize: '14px',
  overflowX: 'auto',
  display: 'block',
})

const StyledThead = styled('thead')({
  backgroundColor: '#f5f5f5',
  fontWeight: 600,
})

const StyledTbody = styled('tbody')({})

const StyledTr = styled('tr')({
  '&:nth-of-type(even)': {
    backgroundColor: '#fafafa',
  },
})

const StyledTh = styled('th')({
  border: '1px solid #e0e0e0',
  padding: '8px 12px',
  textAlign: 'left',
})

const StyledTd = styled('td')({
  border: '1px solid #e0e0e0',
  padding: '8px 12px',
  textAlign: 'left',
})

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isFailedSendMessage = false, messageIndex }) => {
  const [selectedImage, setSelectedImage] = useState<{ src: string; alt: string } | null>(null)

  useEffect(() => {
    return () => {
      if (message.fileAttachments) {
        message.fileAttachments.forEach((file) => {
          if (file.previewUrl) {
            URL.revokeObjectURL(file.previewUrl)
          }
        })
      }
    }
  }, [message.fileAttachments])

  const isStreaming = useSelector(chatDrawerSelectors.getStreaming)
  const isFeedbackEnabled = useFeatureFlag('ada_feedback_enabled', 'on')
  const latestReceivedMessageId = useSelector(chatDrawerSelectors.getLatestReceivedMessageId)
  const messages = useSelector(chatDrawerSelectors.getChatDrawerMessages)

  const latestReceivedMessage = messages.filter((msg) => msg.direction === 'receive').slice(-1)[0]

  const shouldShowCopy =
    message.direction === 'receive' &&
    !isStreaming &&
    !message.toolExecutionState &&
    !(message.buttonActions && message.buttonActions.length > 0) &&
    (message.messageId
      ? message.messageId === latestReceivedMessageId
      : message.origin === 'voice' && latestReceivedMessage === message)

  const shouldShowFeedback = shouldShowCopy && isFeedbackEnabled

  // Check if message has no content and only has a confirmed action
  const hasNoContent = !message.content || message.content.trim().length === 0
  const hasOnlyConfirmedAction =
    message.buttonActions &&
    message.buttonActions.length > 0 &&
    message.buttonActions[0]?.isCollapsed &&
    message.buttonActions[0]?.confirmedActionLabel
  const shouldHideBubble = hasNoContent && hasOnlyConfirmedAction && !message.toolExecutionState

  const isSend = message.direction === 'send'
  const fileAttachments = message.fileAttachments || []

  return (
    <MessageWrapper isSend={isSend}>
      {!shouldHideBubble && (
        <MessageBubbleContainer isSend={isSend} isFailed={isFailedSendMessage}>
          {fileAttachments.length > 0 && <AttachmentBadge fileAttachments={fileAttachments} />}
          <Markdown
            remarkPlugins={[remarkGfm]}
            linkTarget={(url) => {
              if (url.startsWith('http')) {
                return '_blank'
              } else {
                return '_self'
              }
            }}
            components={{
              p: (props) => {
                const { node, ...rest } = props
                return (
                  <p style={{ padding: '0px 0px 0px 0px', marginBlockStart: '2px', marginBlockEnd: '2px' }} {...rest} />
                )
              },
              img: ({ src, alt, ...props }) => {
                if (!src) return null
                return (
                  <ImageContainer>
                    <MessageImage
                      src={src}
                      alt={alt || 'Image'}
                      onClick={() => setSelectedImage({ src, alt: alt || 'Image Preview' })}
                      {...props}
                    />
                    <ImageOverlay>
                      <Icon icon="open_in_new" size="large" color="white" />
                    </ImageOverlay>
                  </ImageContainer>
                )
              },
              table: (props) => {
                const { node, ...rest } = props
                return (
                  <TableWrapper>
                    <StyledTable {...rest}>{props.children}</StyledTable>
                  </TableWrapper>
                )
              },
              thead: (props) => {
                const { node, ...rest } = props
                return <StyledThead {...rest}>{props.children}</StyledThead>
              },
              tbody: (props) => {
                const { node, ...rest } = props
                return <StyledTbody {...rest}>{props.children}</StyledTbody>
              },
              tr: (props) => {
                const { node, ...rest } = props
                return <StyledTr {...rest}>{props.children}</StyledTr>
              },
              th: (props) => {
                const { node, isHeader, children, ...rest } = props
                return <StyledTh {...(rest as any)}>{children}</StyledTh>
              },
              td: (props) => {
                const { node, isHeader, children, ...rest } = props
                return <StyledTd {...(rest as any)}>{children}</StyledTd>
              },
            }}
          >
            {message.content}
          </Markdown>
        </MessageBubbleContainer>
      )}
      {message.toolExecutionState && (
        <ToolProgressRenderer toolExecutionState={message.toolExecutionState} messageIndex={messageIndex} />
      )}
      {!message.toolExecutionState && message.buttonActions && message.buttonActions.length > 0 && (
        <ActionConfirmation buttonActions={message.buttonActions} messageIndex={messageIndex} />
      )}
      {shouldShowCopy && <MessageActionsWrapper message={message} showFeedback={shouldShowFeedback} />}

      {selectedImage && (
        <Modal
          isOpen={!!selectedImage}
          header={selectedImage.alt}
          body={
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          }
          footer={<Button onClick={() => setSelectedImage(null)}>Close</Button>}
        />
      )}
    </MessageWrapper>
  )
}

export default MessageBubble
